export const SUPPORTED_LOCALES = ["de", "en", "fr", "it"] as const;
export type LocaleCode = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: LocaleCode = SUPPORTED_LOCALES[0];

const INTERNAL_LOCALE_PREFIX = new RegExp(`^/(?:${SUPPORTED_LOCALES.join("|")})(?:/|$|\\?|#)`);
const EXTERNAL_OR_SPECIAL_PREFIX = /^(?:[a-zA-Z][a-zA-Z\d+.-]*:|\/\/|#)/;
// Paths the middleware (src/proxy.ts matcher + in-function checks) bypasses entirely —
// keep this in sync with both, or these paths get a locale prefix that won't resolve.
const NON_LOCALIZED_PATH =
  /^\/(?:api|index\.php|monitoring|_next\/static|_next\/image|favicon\.ico)(?:\/|\?|#|$)|\.(?:mp3|png|jpe?g|gif|webp|svg)(?:\?|#|$)/i;

export function isLocaleCode(value: unknown): value is LocaleCode {
  return typeof value === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function resolveCurrentLocale(paramsLang: unknown, pathname: string | null): LocaleCode {
  if (isLocaleCode(paramsLang)) {
    return paramsLang;
  }

  const firstSegment = pathname?.split("/")[1];
  if (isLocaleCode(firstSegment)) {
    return firstSegment;
  }

  return DEFAULT_LOCALE;
}

export function localizePath(pathname: string, locale: LocaleCode): string {
  if (!pathname || EXTERNAL_OR_SPECIAL_PREFIX.test(pathname)) {
    return pathname;
  }

  // Normalize internal relative paths to absolute
  const normalizedPath = pathname.startsWith("/") ? pathname : `/${pathname}`;

  // Skip if already has a locale prefix, or is a path served without one
  if (INTERNAL_LOCALE_PREFIX.test(normalizedPath) || NON_LOCALIZED_PATH.test(normalizedPath)) {
    return normalizedPath;
  }

  if (locale === DEFAULT_LOCALE) {
    return normalizedPath;
  }

  return normalizedPath === "/" ? `/${locale}` : `/${locale}${normalizedPath}`;
}

export function coerceLocale(value: unknown): LocaleCode {
  return isLocaleCode(value) ? value : DEFAULT_LOCALE;
}

// Matches an anchor tag's href attribute: double-quoted, single-quoted, or unquoted.
const ANCHOR_HREF_REGEX = /(<a\b[^>]*?\shref\s*=\s*)("([^"]*)"|'([^']*)'|([^\s>]+))/gi;

// Rewrite internal anchor hrefs in raw CMS HTML to respect the active locale.
// Only root-relative hrefs are rewritten: relative and query-only links keep their
// document-relative resolution, and external/protocol/hash links are left untouched.
export function localizeHtmlAnchors(html: string, locale: LocaleCode): string {
  return html.replace(
    ANCHOR_HREF_REGEX,
    (match, prefix, _value, doubleQuoted, singleQuoted, unquoted) => {
      const url = (doubleQuoted ?? singleQuoted ?? unquoted)?.trim();
      if (!url?.startsWith("/")) {
        return match;
      }
      // Preserve the original quote style so values containing the other quote survive.
      const quote = singleQuoted !== undefined ? "'" : '"';
      return `${prefix}${quote}${localizePath(url, locale)}${quote}`;
    },
  );
}

export function stripLocalePrefix(pathname: string): string {
  if (!pathname) {
    return "/";
  }

  const normalizedPath = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const matchedLocale = SUPPORTED_LOCALES.find(
    (locale) => normalizedPath.startsWith(`/${locale}/`) || normalizedPath === `/${locale}`,
  );

  if (!matchedLocale) {
    return normalizedPath;
  }

  const withoutLocale = normalizedPath.slice(matchedLocale.length + 1);
  return withoutLocale ? `/${withoutLocale.replace(/^\/+/, "")}` : "/";
}
